import { MessagesSquare } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { GulaClient } from "../gulaTypes";
import type { Agent } from "../useAgents";
import { TeamComposer } from "./TeamComposer";
import { TeamMessageItem } from "./TeamMessageItem";
import {
  filterMessages,
  groupByDay,
  isOpenTask,
  type TeamFilter,
} from "./teamChat";
import { useTeamFeed, useTeamRead } from "./useTeamFeed";

/**
 * Inicio: internal chat of the CRM users. Messages, tasks for a teammate and the
 * CRM's own news (sales, deliveries, installations, hot leads, alerts), live.
 */
export const TeamChat = ({
  agents,
  clients,
}: {
  agents: Agent[];
  clients: GulaClient[];
}) => {
  const { messages, refetch, isPending } = useTeamFeed();
  const { meId, lastReadAt, markRead } = useTeamRead();
  const [filter, setFilter] = useState<TeamFilter>("all");
  const listRef = useRef<HTMLDivElement>(null);

  // What was new when the panel opened stays highlighted during the visit
  const [seenBefore, setSeenBefore] = useState<string | null | undefined>();
  useEffect(() => {
    if (lastReadAt !== undefined && seenBefore === undefined) {
      setSeenBefore(lastReadAt);
    }
  }, [lastReadAt, seenBefore]);

  // Being on Inicio with the chat loaded counts as reading it
  const newest = messages[0]?.created_at;
  useEffect(() => {
    if (newest) markRead();
  }, [newest, markRead]);

  const visible = filterMessages(messages, filter, meId);
  const groups = groupByDay(visible);
  const mineCount = filterMessages(messages, "mine", meId).length;
  const pendingCount = messages.filter(isOpenTask).length;

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [visible.length, filter]);

  const tabs: { value: TeamFilter; label: string; count?: number }[] = [
    { value: "all", label: "Todo" },
    { value: "mine", label: "Para mí", count: mineCount },
    { value: "pending", label: "Tareas", count: pendingCount },
  ];

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <MessagesSquare className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Equipo</h2>
      </div>
      <Card className="gap-0 overflow-hidden rounded-3xl py-0">
        <div className="flex gap-1 border-b p-1.5" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={filter === tab.value}
              onClick={() => setFilter(tab.value)}
              className={cn(
                "flex-1 rounded-md px-2 py-1 text-sm transition-colors",
                filter === tab.value
                  ? "bg-muted font-medium"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
            >
              {tab.label}
              {tab.count ? (
                <span
                  className={cn(
                    "ml-1 rounded-full px-1.5 text-xs",
                    tab.value === "mine"
                      ? "bg-amber-200 text-amber-900"
                      : "bg-muted-foreground/15",
                  )}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <div
          ref={listRef}
          className="flex h-[420px] flex-col gap-0.5 overflow-y-auto px-2 py-2"
        >
          {isPending ? (
            <p className="p-2 text-sm text-muted-foreground">Cargando…</p>
          ) : !visible.length ? (
            <p className="p-2 text-sm text-muted-foreground">
              {filter === "all"
                ? "Todavía no hay mensajes."
                : "Nada pendiente por aquí."}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.label} className="flex flex-col gap-0.5">
                <div className="sticky top-0 z-[1] my-1 flex justify-center">
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    {group.label}
                  </span>
                </div>
                {group.messages.map((message) => (
                  <TeamMessageItem
                    key={message.id}
                    message={message}
                    meId={meId}
                    isNew={
                      seenBefore !== undefined &&
                      String(message.sales_id) !== String(meId) &&
                      (!seenBefore || message.created_at > seenBefore)
                    }
                    onChanged={refetch}
                  />
                ))}
              </div>
            ))
          )}
        </div>

        <TeamComposer
          agents={agents}
          meId={meId}
          clients={clients}
          onSent={refetch}
        />
      </Card>
    </section>
  );
};
